import { z } from 'zod';
import { parseEmailExtracted } from './agent-panel';

// Persisted FSM state — must mirror inbox.thread_status_enum exactly.
export const inboxThreadStatusEnum = z.enum([
  'new',
  'awaiting-reply',
  'reply-pasted',
  'ready-to-build',
  'hold',
  'done',
  'closed',
]);
export type InboxThreadStatus = z.infer<typeof inboxThreadStatusEnum>;

// Derived at read time — `stalled` is the awaiting-reply > 3 days case.
export const inboxThreadDerivedStatusEnum = z.union([
  inboxThreadStatusEnum,
  z.literal('stalled'),
]);
export type InboxThreadDerivedStatus = z.infer<
  typeof inboxThreadDerivedStatusEnum
>;

// Roles on inbox.thread_message — text + CHECK so v3 can add values cheaply.
export const inboxThreadMessageRoleEnum = z.enum([
  'pasted-customer',
  'drafted-by-consultant',
  'sent-marker',
  'follow-up-reminder',
]);
export type InboxThreadMessageRole = z.infer<typeof inboxThreadMessageRoleEnum>;

// `brief_provenance` is a free-form map { fieldPath: 'consultant' | `ai-from-message-${n}` }.
// We don't enforce key membership here — the service writes only known paths.
export const inboxBriefProvenanceSchema = z
  .record(
    z.string(),
    z.union([
      z.literal('consultant'),
      z.string().regex(/^ai-from-message-\d+$/),
    ])
  )
  .nullable();
export type InboxBriefProvenance = z.infer<typeof inboxBriefProvenanceSchema>;

// `brief` is the parseEmailExtracted shape (same Zod is reused) or null.
export const inboxThreadBriefSchema = parseEmailExtracted.nullable();
export type InboxThreadBrief = z.infer<typeof inboxThreadBriefSchema>;

// Row shape on the wire (`inbox.thread`). camelCase mirroring Drizzle.
// `customerName`/`customerEmail` are NOT stored on the row — they are
// always projected from crm.v_customer at read time. The service joins
// v_customer.id = thread.customer_id and copies name/email into the
// returned shape so the UI doesn't need to do its own lookups.
const inboxThreadCore = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  customerId: z.uuid(),
  itineraryId: z.uuid().nullable(),
  customerName: z.string(),
  customerEmail: z.string(),
  subject: z.string().nullable(),
  status: inboxThreadStatusEnum,
  brief: inboxThreadBriefSchema,
  briefProvenance: inboxBriefProvenanceSchema,
  // Consultant-curated taxonomy hashtag ids (sibling to brief, not part
  // of the LLM-extracted shape). Null until the consultant touches the
  // picker; empty list = explicit "no tags".
  selectedHashtagIds: z.array(z.string()).nullable(),
  createdOn: z.iso.datetime({ offset: true }),
  modifiedOn: z.iso.datetime({ offset: true }).nullable(),
  lastPasteAt: z.iso.datetime({ offset: true }).nullable(),
  lastDraftedAt: z.iso.datetime({ offset: true }).nullable(),
  lastSentAt: z.iso.datetime({ offset: true }).nullable(),
  closedOn: z.iso.datetime({ offset: true }).nullable(),
});
export const inboxThreadSchema = inboxThreadCore;
export type InboxThread = z.infer<typeof inboxThreadSchema>;

// A file uploaded with a pasted customer email (agent-platform N1-1). The key
// names a PRIVATE UploadThing file: never a URL, never shown as a link — the
// agent reads it server-side through a short-lived signed URL.
export const INBOX_MAX_ATTACHMENTS = 10;
export const inboxMessageAttachmentInput = z.object({
  key: z.string().min(1).max(200),
  name: z.string().trim().min(1).max(255),
  mime: z.string().max(200).optional(),
  size: z.number().int().nonnegative().optional(),
  /** The upload route's signature of the key to its uploader. */
  proof: z.string().min(1).max(200),
});
export type InboxMessageAttachmentInput = z.infer<
  typeof inboxMessageAttachmentInput
>;

export const inboxMessageAttachmentSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  mime: z.string().nullable(),
  sizeBytes: z.number().int().nullable(),
});
export type InboxMessageAttachment = z.infer<
  typeof inboxMessageAttachmentSchema
>;

export const inboxThreadMessageSchema = z.object({
  id: z.uuid(),
  threadId: z.uuid(),
  role: inboxThreadMessageRoleEnum,
  body: z.string(),
  sourceIndex: z.number().int().nonnegative(),
  pastedAt: z.iso.datetime({ offset: true }).nullable(),
  draftedAt: z.iso.datetime({ offset: true }).nullable(),
  sentMarkedAt: z.iso.datetime({ offset: true }).nullable(),
  parseExtracted: inboxThreadBriefSchema,
  createdOn: z.iso.datetime({ offset: true }),
  attachments: z.array(inboxMessageAttachmentSchema).default([]),
});
export type InboxThreadMessage = z.infer<typeof inboxThreadMessageSchema>;

// Compact row for the inbox list — adds derived `stalled` math + last-message preview.
export const inboxThreadSummarySchema = z.object({
  id: z.uuid(),
  customerName: z.string(),
  customerEmail: z.string(),
  subject: z.string().nullable(),
  status: inboxThreadStatusEnum,
  derivedStatus: inboxThreadDerivedStatusEnum,
  stalledDays: z.number().int().nullable(),
  briefOneLiner: z.string().nullable(),
  lastMessagePreview: z.string().nullable(),
  lastActivityAt: z.iso.datetime({ offset: true }),
  pastedMessagesCount: z.number().int().nonnegative(),
});
export type InboxThreadSummary = z.infer<typeof inboxThreadSummarySchema>;

// `byId` returns the persisted row plus the derived projection — UI uses both.
export const inboxThreadDetailSchema = inboxThreadCore.extend({
  derivedStatus: inboxThreadDerivedStatusEnum,
  stalledDays: z.number().int().nullable(),
});
export type InboxThreadDetail = z.infer<typeof inboxThreadDetailSchema>;

// --- Procedure inputs ---

export const listInboxThreadsInput = z.object({
  search: z.string().trim().optional(),
  status: inboxThreadStatusEnum.optional(),
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(20),
});
export type ListInboxThreadsInput = z.infer<typeof listInboxThreadsInput>;

export const listInboxThreadsOutput = z.object({
  items: z.array(inboxThreadSummarySchema),
  counters: z.object({
    total: z.number().int().nonnegative(),
    needsDraft: z.number().int().nonnegative(),
  }),
  nextCursor: z.string().nullable(),
});
export type ListInboxThreadsOutput = z.infer<typeof listInboxThreadsOutput>;

export const getInboxThreadByIdInput = z.object({ id: z.uuid() });
export type GetInboxThreadByIdInput = z.infer<typeof getInboxThreadByIdInput>;

export const getInboxThreadByIdOutput = z.object({
  thread: inboxThreadDetailSchema,
  messages: z.array(inboxThreadMessageSchema),
});
export type GetInboxThreadByIdOutput = z.infer<typeof getInboxThreadByIdOutput>;

// Every thread links to a customer in CRM (either a contact-customer or
// a company-customer — both surface through crm.v_customer with distinct
// `crmType` values). The compose form picks an existing customer or
// creates one (planned Phase 2b: company → contact create chain), then
// passes the resulting customerId here.
export const createInboxThreadFromPasteInput = z.object({
  customerId: z.uuid(),
  subject: z.string().max(500).optional(),
  emailBody: z.string().min(20).max(10_000),
  attachments: z
    .array(inboxMessageAttachmentInput)
    .max(INBOX_MAX_ATTACHMENTS)
    .optional(),
});
export type CreateInboxThreadFromPasteInput = z.infer<
  typeof createInboxThreadFromPasteInput
>;

export const createInboxThreadFromPasteOutput = z.object({
  id: z.uuid(),
});

// AI subject suggestion for the compose form — runs before a thread exists,
// so it takes the raw pasted email body rather than a threadId.
export const suggestInboxThreadSubjectInput = z.object({
  emailBody: z.string().min(20).max(10_000),
});
export type SuggestInboxThreadSubjectInput = z.infer<
  typeof suggestInboxThreadSubjectInput
>;

export const suggestInboxThreadSubjectOutput = z.object({
  subject: z.string(),
});
export type SuggestInboxThreadSubjectOutput = z.infer<
  typeof suggestInboxThreadSubjectOutput
>;

export const appendInboxThreadMessageInput = z.object({
  threadId: z.uuid(),
  role: z.literal('pasted-customer'),
  body: z.string().min(1).max(10_000),
  attachments: z
    .array(inboxMessageAttachmentInput)
    .max(INBOX_MAX_ATTACHMENTS)
    .optional(),
});
export type AppendInboxThreadMessageInput = z.infer<
  typeof appendInboxThreadMessageInput
>;

export const appendInboxThreadMessageOutput = z.object({
  thread: inboxThreadDetailSchema,
  briefDelta: z
    .object({
      // Field paths whose provenance flipped on this paste.
      changed: z.array(z.string()),
    })
    .nullable(),
});

export const markInboxThreadSentInput = z.object({
  threadId: z.uuid(),
  draftBody: z.string().min(1).max(20_000),
  intent: z.enum(['reply', 'itinerary-email']),
});
export type MarkInboxThreadSentInput = z.infer<typeof markInboxThreadSentInput>;

export const markInboxThreadSentOutput = z.object({
  thread: inboxThreadDetailSchema,
});

export const setInboxThreadStatusInput = z.object({
  threadId: z.uuid(),
  status: inboxThreadStatusEnum,
});
export type SetInboxThreadStatusInput = z.infer<
  typeof setInboxThreadStatusInput
>;

export const setInboxThreadStatusOutput = z.object({
  thread: inboxThreadDetailSchema,
});

export const updateInboxThreadBriefInput = z.object({
  threadId: z.uuid(),
  // Partial overlay: any subset of brief fields the consultant edited.
  brief: parseEmailExtracted.partial(),
});
export type UpdateInboxThreadBriefInput = z.infer<
  typeof updateInboxThreadBriefInput
>;

export const updateInboxThreadBriefOutput = z.object({
  thread: inboxThreadDetailSchema,
});

// Thread → itinerary creation (PRD FR-3.5 / Open Q3). Idempotent: the
// service returns the thread's existing `itinerary_id` if one is already
// linked, otherwise it creates an itinerary + active version in a single
// advisory-locked transaction. Re-introduced for the Agent Workspace
// "Start day picks" CTA after the email-flow cleanup removed the original.
export const ensureInboxThreadItineraryInput = z.object({
  threadId: z.uuid(),
});
export type EnsureInboxThreadItineraryInput = z.infer<
  typeof ensureInboxThreadItineraryInput
>;

export const ensureInboxThreadItineraryOutput = z.object({
  itineraryId: z.uuid(),
});
export type EnsureInboxThreadItineraryOutput = z.infer<
  typeof ensureInboxThreadItineraryOutput
>;

export const inferHashtagsInput = z.object({ threadId: z.uuid() });
export type InferHashtagsInput = z.infer<typeof inferHashtagsInput>;

export const matchedHashtagSchema = z.object({
  id: z.string(),
  name: z.string(),
  category: z.string(),
});
export type MatchedHashtag = z.infer<typeof matchedHashtagSchema>;

export const inferHashtagsOutput = z.object({
  hashtags: z.array(matchedHashtagSchema),
});
export type InferHashtagsOutput = z.infer<typeof inferHashtagsOutput>;

export const listOrgHashtagsOutput = z.object({
  hashtags: z.array(matchedHashtagSchema),
});
export type ListOrgHashtagsOutput = z.infer<typeof listOrgHashtagsOutput>;

export const setSelectedHashtagsInput = z.object({
  threadId: z.uuid(),
  hashtagIds: z.array(z.string()),
});
export type SetSelectedHashtagsInput = z.infer<typeof setSelectedHashtagsInput>;

export const setSelectedHashtagsOutput = z.object({
  selectedHashtagIds: z.array(z.string()),
});
export type SetSelectedHashtagsOutput = z.infer<
  typeof setSelectedHashtagsOutput
>;
